import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ContentRecord,
  DeployItem,
  GeneratedContentDetails,
} from '@cyborg-monorepo/hunter/common';
import { SupportedTools } from '@cyborg-monorepo/hunter/matchboi';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { CompanyService } from '../../services/company.service';
import { ContentService } from '../../services/content.service';
import {
  MarkDeployedComponent,
  MarkRemovedComponent,
} from '../cyborg-snackbar/cyborg-snackbar';

@Component({ standalone: false, 
  selector: 'deployment-queue',
  templateUrl: './deployment-queue.html',
  styleUrls: ['./deployment-queue.scss'],
})
export class DeploymentQueueComponent implements OnInit, AfterViewInit {
  @ViewChild('drawer') public sidenav: MatSidenav;
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @Input() public deployItem: DeployItem;
  @Output('onMarkForDeployedEvent')
  public onMarkForDeployedEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output('onRemoveDeployedEvent')
  public onRemoveDeployedEvent: EventEmitter<void> = new EventEmitter<void>();
  private compatibleContentSet: Set<ContentRecord> = new Set();
  public compatibleContent: Array<ContentRecord>;
  public contents: Array<ContentRecord>;
  public contentDetails: ContentRecord;
  public contentView: string;
  private tool: SupportedTools = localStorage.getItem('tool') as SupportedTools;
  private otherTools: Array<string> = [
    'CrowdStrike',
    'Snort',
    'Suricata',
    'CarbonBlack',
    'Microsoft Defender',
  ];
  private categories = JSON.parse(localStorage.getItem('categories'));
  public staged = false;
  public deployed = true;
  public notFullyMatched: boolean;
  public generatedContentDetails: GeneratedContentDetails;
  public outdated: boolean = false;
  public latestHuntPackageChangeLog: {
    date: string;
    note: string;
  };
  public latestContentChangeLog: Array<{
    tool: string;
    goal: string;
    categories: string;
    date: string;
    note: string;
  }>;
  public displayedColumns: string[] = [
    'date',
    'tool',
    'goal',
    'categories',
    'note',
  ];
  public environmentContent;

  constructor(
    private companyService: CompanyService,
    private snackBar: MatSnackBar,
    private contentService: ContentService,
  ) {}

  public ngOnInit(): void {
    this.getCompatibleContent();
    this.getOutdated();
    this.environmentContent = this.contentService.getHuntPackageContent(
      this.deployItem.huntPackageUUID,
    );
  }

  public ngAfterViewInit(): void {
    this.tabstrip.selectTab(0);
  }

  public getOutdated(): void {
    if (
      this.deployItem.deployedAt &&
      new Date(this.deployItem.deployedAt) <
        new Date(this.deployItem.huntPackage.last_updated)
    ) {
      this.outdated = true;
    }
  }

  public getCompatibleContent(): void {
    this.contents = this.deployItem.huntPackage.content.records;
    this.contents
      .filter((contentRecord) => contentRecord.tool === this.tool)
      .some((contentRecord) => {
        contentRecord.deployment_requirements.categories.forEach((cat) => {
          if (this.categories.includes(cat)) {
            this.compatibleContentSet.add(contentRecord);
          }
        });
      });
    this.compatibleContent = Array.from(this.compatibleContentSet);

    // Adding due to not tracking Tools outside of mapping (Only SIEM)
    const otherContent = this.contents.filter((element) => {
      return this.otherTools.includes(element.tool);
    });
    this.compatibleContent.push(...otherContent);
  }

  public openContentDetails(content: ContentRecord): void {
    this.sidenav.open();
    this.contentDetails = content;
  }

  // Could not figure out how to inject this
  public getContent(content: ContentRecord): void {
    this.contentService.getContent(content).subscribe((generatedContent) => {
      this.generatedContentDetails = generatedContent;
    });
  }

  public openDeployContent(content): void {
    this.sidenav.open();
    this.getContent(content);
  }

  public openChangeLog(): void {
    this.sidenav.open();
    this.latestHuntPackageChangeLog =
      this.deployItem.huntPackage.change_log[
        this.deployItem.huntPackage.change_log.length - 1
      ];
    this.latestContentChangeLog = this.deployItem.huntPackage.content.records
      .filter((content) => {
        return (
          content.change_log[content.change_log.length - 1].note !== 'Initial'
        );
      })
      .map((content) => {
        const lastChange = content.change_log[content.change_log.length - 1];
        return {
          tool: content.tool,
          goal: content.goal,
          categories: content.deployment_requirements.categories.join(', '),
          date: lastChange.date,
          note: lastChange.note,
        };
      });
  }

  public close(): void {
    this.sidenav.close();
    this.contentDetails = null;
    this.generatedContentDetails = null;
    this.latestHuntPackageChangeLog = null;
  }

  public markAsDeployed(huntPackageUUID: string): void {
    this.snackBar.openFromComponent(MarkDeployedComponent, {
      duration: 5 * 1000,
    });
    this.companyService.markAsDeployed(huntPackageUUID).subscribe(() => {
      this.onMarkForDeployedEvent.emit();
    });
  }

  public removeDeployed(huntPackageUUID: string): void {
    this.snackBar.openFromComponent(MarkRemovedComponent, {
      duration: 5 * 1000,
    });
    this.companyService.removeDeployed(huntPackageUUID).subscribe(() => {
      this.onMarkForDeployedEvent.emit();
    });
  }
}
